import { useState, useEffect, useCallback, useRef } from 'react';

interface UseVariableCompletionReturn {
  isOpen: boolean;
  query: string;
  position: { top: number; left: number } | null;
  onSelect: (variableKey: string) => void;
  onClose: () => void;
  commandMenuRef: React.RefObject<any>;
}

interface UseVariableCompletionOptions {
  editorRef: React.RefObject<HTMLElement>;
  onInsert: (variableKey: string) => void;
  /** Optional key that triggers re-initialization when changed (e.g., templateType) */
  reinitKey?: string | number;
}

export function useVariableCompletion({
  editorRef,
  onInsert,
  reinitKey,
}: UseVariableCompletionOptions): UseVariableCompletionReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const slashRangeRef = useRef<{ start: number; end: number } | null>(null);
  const commandMenuRef = useRef<any>(null);

  // Get cursor position in contenteditable
  const getCursorPosition = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;

    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();

    return {
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    };
  }, []);

  // Get text content and cursor offset in contenteditable
  const getTextAndOffset = useCallback(() => {
    if (!editorRef.current) return null;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;

    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(editorRef.current);
    preCaretRange.setEnd(range.endContainer, range.endOffset);

    const textContent = editorRef.current.textContent || '';
    const offset = preCaretRange.toString().length;

    return { textContent, offset };
  }, [editorRef]);

  // Handle selection
  const handleSelect = useCallback(
    (variableKey: string) => {
      if (!editorRef.current || !slashRangeRef.current) return;

      const selection = window.getSelection();
      if (!selection) return;

      // Create a range for the slash and query text
      const range = document.createRange();
      const textNode = findTextNode(editorRef.current, slashRangeRef.current.start);

      if (textNode) {
        const offset = slashRangeRef.current.start;
        const length = slashRangeRef.current.end - slashRangeRef.current.start;

        range.setStart(textNode.node, textNode.offset);
        range.setEnd(textNode.node, textNode.offset + length);

        selection.removeAllRanges();
        selection.addRange(range);

        // Delete the slash and query
        range.deleteContents();
      }

      // Insert the variable
      onInsert(variableKey);

      // Close the menu
      setIsOpen(false);
      setQuery('');
      slashRangeRef.current = null;
    },
    [editorRef, onInsert]
  );

  // Close the menu
  const handleClose = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    slashRangeRef.current = null;
  }, []);

  // Listen for keyboard events
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) {
      console.log('🔍 useVariableCompletion: No editor ref');
      return;
    }

    console.log('🔍 useVariableCompletion: Setting up listeners on', editor);

    const handleInput = () => {
      console.log('🔍 useVariableCompletion: Input event fired');
      const data = getTextAndOffset();
      if (!data) {
        console.log('🔍 useVariableCompletion: No text/offset data');
        return;
      }

      const { textContent, offset } = data;
      console.log('🔍 useVariableCompletion: textContent:', textContent, 'offset:', offset);

      // Find the last '/' before the cursor
      let slashIndex = -1;
      for (let i = offset - 1; i >= 0; i--) {
        const char = textContent[i];
        if (char === '/') {
          slashIndex = i;
          break;
        }
        // Stop searching if we hit a space or newline
        if (char === ' ' || char === '\n') {
          break;
        }
      }

      console.log('🔍 useVariableCompletion: slashIndex:', slashIndex);

      if (slashIndex >= 0) {
        // Extract the query after '/'
        const queryText = textContent.substring(slashIndex + 1, offset);
        console.log('🔍 useVariableCompletion: queryText:', queryText);

        // Only show if query doesn't contain spaces or newlines
        if (!queryText.includes(' ') && !queryText.includes('\n')) {
          console.log('🔍 useVariableCompletion: Opening menu with query:', queryText);
          setQuery(queryText);
          setIsOpen(true);
          slashRangeRef.current = { start: slashIndex, end: offset };

          // Update position
          const pos = getCursorPosition();
          console.log('🔍 useVariableCompletion: Cursor position:', pos);
          if (pos) {
            setPosition(pos);
          }
          return;
        }
      }

      // Close if no valid slash found
      if (isOpen) {
        handleClose();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
        return;
      }

      // Forward keyboard events to the Command component
      if (['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key) && commandMenuRef.current?.onKeyDown) {
        const handled = commandMenuRef.current.onKeyDown(e);
        if (handled) {
          e.preventDefault();
        }
      }
    };

    editor.addEventListener('input', handleInput);
    editor.addEventListener('keydown', handleKeyDown);

    return () => {
      editor.removeEventListener('input', handleInput);
      editor.removeEventListener('keydown', handleKeyDown);
    };
  }, [editorRef, isOpen, getCursorPosition, getTextAndOffset, handleClose, reinitKey]);

  return {
    isOpen,
    query,
    position,
    onSelect: handleSelect,
    onClose: handleClose,
    commandMenuRef,
  };
}

// Helper to find text node and offset within contenteditable
function findTextNode(
  element: HTMLElement,
  targetOffset: number
): { node: Text; offset: number } | null {
  let currentOffset = 0;

  function traverse(node: Node): { node: Text; offset: number } | null {
    if (node.nodeType === Node.TEXT_NODE) {
      const textNode = node as Text;
      const length = textNode.textContent?.length || 0;

      if (currentOffset + length >= targetOffset) {
        return {
          node: textNode,
          offset: targetOffset - currentOffset,
        };
      }

      currentOffset += length;
    } else {
      for (let i = 0; i < node.childNodes.length; i++) {
        const result = traverse(node.childNodes[i]);
        if (result) return result;
      }
    }

    return null;
  }

  return traverse(element);
}
